import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  decideWeekendRequest,
  decideWeekendTask,
  getWeekendRequestsToDecide,
  getWeekendTasksToDecide,
} from '../../services/WeekendWorkingService';
import PaginationComponent from '../../components/Pagination/Pagination';
import { ERROR_KEY, SHOW_NOTIFICATION, SUCCESS_KEY } from '../../constants';
import { LabelledText, WeekendCard } from './WeekendCard';
import {
  STATUS_LABEL,
  datesText,
  formatDate,
  lastDecision,
  toMoment,
} from './weekendUtils';

const PAGE_SIZE = 5;
const FETCH_SIZE = 100;
const HOUR_MS = 3600 * 1000;
const TYPES = [
  ['ALL', 'All'],
  ['REQUEST', 'Date requests'],
  ['TASK', 'Task hours'],
];

/**
 * Project manager inbox, one list like the WFH tab: weekend date requests and the hours
 * logged on approved dates, both waiting for the signed-in manager.
 */
const WeekendApprovalsInbox = () => {
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [status, setStatus] = useState('PENDING');
  const [type, setType] = useState('ALL');
  const [page, setPage] = useState(1);
  const [rejecting, setRejecting] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const notify = useCallback(
    (kind, message) =>
      dispatch({
        type: SHOW_NOTIFICATION,
        data: { open: true, type: kind, message },
      }),
    [dispatch],
  );

  const load = useCallback(async () => {
    const [reqRes, taskRes] = await Promise.all([
      getWeekendRequestsToDecide({ status, pageSize: FETCH_SIZE }),
      getWeekendTasksToDecide({ status, pageSize: FETCH_SIZE }),
    ]);
    if (reqRes.isError === false) {
      setRequests(reqRes.data?.data?.content || []);
    } else {
      notify(reqRes.type || ERROR_KEY, reqRes.message);
    }
    if (taskRes.isError === false) {
      setTasks(taskRes.data?.data?.content || []);
    } else {
      notify(taskRes.type || ERROR_KEY, taskRes.message);
    }
  }, [status, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => {
    const all = [
      ...(type === 'TASK'
        ? []
        : requests.map((item) => ({
            kind: 'REQUEST',
            item,
            sortDate: toMoment((item.dates || [])[0]),
          }))),
      ...(type === 'REQUEST'
        ? []
        : tasks.map((item) => ({
            kind: 'TASK',
            item,
            sortDate: toMoment(item.workDate),
          }))),
    ];
    return all.sort((a, b) => b.sortDate.valueOf() - a.sortDate.valueOf());
  }, [requests, tasks, type]);

  const totalPages = Math.max(Math.ceil(items.length / PAGE_SIZE), 1);
  const shown = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const decide = async ({ kind, item }, decision, text) => {
    setBusy(true);
    const res =
      kind === 'TASK'
        ? await decideWeekendTask(item.id, decision, text)
        : await decideWeekendRequest(item.id, decision, text);
    setBusy(false);
    if (res.isError === false) {
      notify(
        SUCCESS_KEY,
        `${kind === 'TASK' ? 'Task' : 'Request'} ${
          decision === 'APPROVED' ? 'approved' : 'rejected'
        }`,
      );
      setRejecting(null);
      setComment('');
      load();
    } else {
      notify(res.type || ERROR_KEY, res.message);
    }
  };

  const actionsFor = (entry) =>
    entry.item.status === 'PENDING' && (
      <>
        <Button
          variant="outlined"
          size="small"
          color="error"
          sx={{ px: 3 }}
          disabled={busy}
          onClick={() => setRejecting(entry)}
        >
          Reject
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="success"
          sx={{ px: 3 }}
          disabled={busy}
          onClick={() => decide(entry, 'APPROVED')}
        >
          Approve
        </Button>
      </>
    );

  const renderRequest = (entry) => {
    const request = entry.item;
    const decision = lastDecision(request);
    return (
      <WeekendCard
        key={`request-${request.id}`}
        title={request.employeeName}
        dateText={datesText(request.dates)}
        chips={['Date request', request.projectName]}
        status={request.status}
        actions={actionsFor(entry)}
      >
        <LabelledText label="Dates">
          {(request.dates || []).map((date) => formatDate(date)).join(', ')}
        </LabelledText>
        {request.reason && (
          <LabelledText label="Reason">{request.reason}</LabelledText>
        )}
        {decision && (
          <LabelledText label="Decision">
            {STATUS_LABEL[decision.status]} by{' '}
            {decision.managerName || 'manager'}
            {decision.comment ? ` — ${decision.comment}` : ''}
          </LabelledText>
        )}
      </WeekendCard>
    );
  };

  const renderTask = (entry) => {
    const task = entry.item;
    return (
      <WeekendCard
        key={`task-${task.id}`}
        title={task.employeeName}
        dateText={formatDate(task.workDate)}
        chips={[
          'Task hours',
          task.projectName,
          `${Number(((task.requestedHours || 0) / HOUR_MS).toFixed(2))} hrs`,
        ]}
        status={task.status}
        actions={actionsFor(entry)}
      >
        <LabelledText label="Task Title">{task.title}</LabelledText>
        <LabelledText label="Task Description">{task.description}</LabelledText>
        {task.status !== 'PENDING' && (
          <LabelledText label="Decision">
            {STATUS_LABEL[task.status]} by {task.decidedByName || 'manager'}
            {task.comment ? ` — ${task.comment}` : ''}
          </LabelledText>
        )}
      </WeekendCard>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" sx={{ mr: 'auto' }}>
          Weekend Working
        </Typography>
        <TextField
          select
          size="small"
          label="Type"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 150 }}
        >
          {TYPES.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.keys(STATUS_LABEL).map((key) => (
            <MenuItem key={key} value={key}>
              {STATUS_LABEL[key]}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {shown.length === 0 && (
        <Typography color="text.secondary">
          No weekend working items to show.
        </Typography>
      )}

      {shown.map((entry) =>
        entry.kind === 'TASK' ? renderTask(entry) : renderRequest(entry),
      )}

      {totalPages > 1 && (
        <Box sx={{ mt: 2 }}>
          <PaginationComponent
            count={totalPages}
            page={page}
            pageChange={(e, value) => setPage(value)}
          />
        </Box>
      )}

      <Dialog
        open={Boolean(rejecting)}
        onClose={() => setRejecting(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Reject {rejecting?.kind === 'TASK' ? 'task' : 'request'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={3}
            size="small"
            label="Reason for rejecting"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || comment.trim() === ''}
            onClick={() => decide(rejecting, 'REJECTED', comment.trim())}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeekendApprovalsInbox;
